// src/services/correiosService.js
import { pb } from "./pocketbase";

const FALLBACK_KEY = "correios_config_fallback";

// Webhook centralizador no n8n para evitar CORS e proteger credenciais no Frontend
const N8N_CORREIOS_WEBHOOK_URL = "https://n8n.semprejoias.com.br/webhook/gestao-correios-integracao";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const correiosService = {
  /**
   * Busca as configurações dos Correios salvas no PocketBase.
   * Se a coleção não existir ou ocorrer outro erro, utiliza o localStorage como fallback.
   */
  async fetchConfig() {
    try {
      const records = await pb.collection("correios_configs").getFullList();
      if (records.length > 0) {
        return records[0];
      }
    } catch (error) {
      console.warn("[CorreiosService] Coleção 'correios_configs' indisponível no PocketBase. Usando localStorage fallback.", error);
    }
    const fallback = localStorage.getItem(FALLBACK_KEY);
    return fallback ? JSON.parse(fallback) : null;
  },

  /**
   * Salva as configurações dos Correios no PocketBase ou no localStorage como fallback.
   */
  async saveConfig(data) {
    const configData = {
      api_user: data.api_user,
      api_password: data.api_password,
      cartao_postagem: data.cartao_postagem,
      codigo_administrativo: data.codigo_administrativo,
      remetente_nome: data.remetente_nome,
      remetente_cnpj: data.remetente_cnpj,
      cep: data.cep,
      logradouro: data.logradouro,
      numero: data.numero,
      complemento: data.complemento || "",
      bairro: data.bairro,
      cidade: data.cidade,
      uf: data.uf,
      ddd_celular: data.ddd_celular,
      celular: data.celular,
      email: data.email,
      peso_padrao: String(data.peso_padrao || "100"),
      comprimento: String(data.comprimento || "17"),
      largura: String(data.largura || "5"),
      altura: String(data.altura || "12"),
      servico_padrao: data.servico_padrao || "03298",
      correios_token: "",
      correios_token_expira_em: ""
    };

    try {
      let existing = [];
      try {
        existing = await pb.collection("correios_configs").getFullList();
      } catch (e) {
        throw new Error("Coleção inacessível");
      }

      if (existing.length > 0) {
        return await pb.collection("correios_configs").update(existing[0].id, configData);
      } else {
        return await pb.collection("correios_configs").create(configData);
      }
    } catch (error) {
      console.warn("[CorreiosService] Não foi possível salvar no PocketBase. Salvando no localStorage fallback.", error);
      const mockConfig = { id: "local_fallback", ...configData };
      localStorage.setItem(FALLBACK_KEY, JSON.stringify(mockConfig));
      return mockConfig;
    }
  },

  /**
   * Autentica na API dos Correios delegando a requisição para o n8n.
   * Usado para fins de teste de conexão no painel de configurações.
   */
  async authenticate(user, password, cartaoPostagem) {
    const response = await fetch(N8N_CORREIOS_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "auth",
        user,
        password,
        cartaoPostagem
      }),
    });

    if (!response.ok) {
      let errText = "";
      try {
        const errJson = await response.json();
        errText = errJson.mensagem || errJson.msgs?.[0] || JSON.stringify(errJson);
      } catch (e) {
        errText = await response.text();
      }
      throw new Error(`Erro de Autenticação via n8n: ${errText || response.statusText}`);
    }

    const data = await response.json();
    const token = data.token;
    if (!token) {
      throw new Error("Token não retornado pelo fluxo do n8n.");
    }

    return token;
  },

  /**
   * Cria uma pré-postagem delegando a requisição para o n8n.
   */
  async criarPrePostagem(dadosPedido) {
    const config = await this.fetchConfig();
    if (!config) {
      throw new Error("Configurações dos Correios não encontradas.");
    }

    const cleanRemetenteCelular = (config.celular || "").replace(/\D/g, "");
    const remetenteCelularFinal = cleanRemetenteCelular.length === 8 ? "9" + cleanRemetenteCelular : cleanRemetenteCelular;

    const cleanDestinatarioCelular = (dadosPedido.destinatario.celular || "").replace(/\D/g, "");
    const destinatarioCelularFinal = cleanDestinatarioCelular.length === 8 ? "9" + cleanDestinatarioCelular : cleanDestinatarioCelular;

    const listaServicoAdicional = [];
    if (dadosPedido.ar) {
      listaServicoAdicional.push({ codigoServicoAdicional: "001" });
    }
    if (dadosPedido.seguro) {
      const codigoServico = dadosPedido.codigoServico || config.servico_padrao || "03298";
      const isPac = codigoServico === "03298";
      listaServicoAdicional.push({
        codigoServicoAdicional: isPac ? "064" : "019",
        valorDeclarado: String(Number(dadosPedido.valorSegurado || dadosPedido.valorDeclaracao).toFixed(2))
      });
    }

    const cleanChaveNFe = (dadosPedido.chaveNFe || "").replace(/\D/g, "");
    let cleanNumeroNFe = String(dadosPedido.numeroNotaFiscal || "").replace(/\D/g, "");
    if (!cleanNumeroNFe && cleanChaveNFe.length === 44) {
      cleanNumeroNFe = String(Number(cleanChaveNFe.slice(25, 34)));
    }

    if (cleanChaveNFe.length !== 44 && dadosPedido.logisticaReversa !== "S") {
      throw new Error(
        "Não é possível gerar a pré-postagem sem Nota Fiscal (NF-e) autorizada. Devido à regra do CONFAZ para contribuintes de ICMS, emita e autorize a NF-e antes de gerar a etiqueta."
      );
    }

    const payload = {
      idCorreios: config.api_user,
      codigoServico: dadosPedido.codigoServico || config.servico_padrao || "03298",
      numeroCartaoPostagem: config.cartao_postagem,
      codigoAdministrativo: config.codigo_administrativo || "",
      ...(cleanChaveNFe ? { chaveNFe: cleanChaveNFe } : {}),
      ...(cleanNumeroNFe ? { numeroNotaFiscal: cleanNumeroNFe } : {}),
      remetente: {
        nome: config.remetente_nome,
        cpfCnpj: config.remetente_cnpj,
        dddCelular: config.ddd_celular,
        celular: remetenteCelularFinal,
        email: config.email,
        endereco: {
          cep: config.cep,
          logradouro: config.logradouro,
          numero: config.numero,
          complemento: config.complemento || "",
          bairro: config.bairro,
          cidade: config.cidade,
          uf: config.uf
        }
      },
      destinatario: {
        nome: dadosPedido.destinatario.nome,
        cpfCnpj: dadosPedido.destinatario.cpfCnpj,
        dddCelular: dadosPedido.destinatario.dddCelular,
        celular: destinatarioCelularFinal,
        email: dadosPedido.destinatario.email,
        endereco: {
          cep: dadosPedido.destinatario.endereco.cep,
          logradouro: dadosPedido.destinatario.endereco.logradouro,
          numero: dadosPedido.destinatario.endereco.numero,
          complemento: dadosPedido.destinatario.endereco.complemento || "",
          bairro: dadosPedido.destinatario.endereco.bairro,
          cidade: dadosPedido.destinatario.endereco.cidade,
          uf: dadosPedido.destinatario.endereco.uf
        }
      },
      pesoInformado: String(dadosPedido.pesoInformado || config.peso_padrao || "100"),
      codigoFormatoObjetoInformado: "2",
      comprimentoInformado: String(dadosPedido.comprimentoInformado || config.comprimento || "17"),
      larguraInformada: String(dadosPedido.larguraInformada || config.largura || "5"),
      alturaInformada: String(dadosPedido.alturaInformada || config.altura || "12"),
      cienteObjetoNaoProibido: 1,
      ...(listaServicoAdicional.length > 0 && { listaServicoAdicional }),
      itensDeclaracaoConteudo: dadosPedido.itensDeclaracaoConteudo || [
        {
          conteudo: dadosPedido.conteudoDeclaracao || "Joia em prata 925",
          quantidade: String(dadosPedido.quantidadeDeclaracao || "1"),
          valor: String(dadosPedido.valorDeclaracao || "219.99")
        }
      ]
    };

    let response = await fetch(N8N_CORREIOS_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "criarPrePostagem",
        payload
      }),
    });

    if (!response.ok) {
      let errText = "";
      try {
        const errJson = await response.json();
        if (errJson.errorMessage?.includes("Forbidden") || errJson.errorDescription?.includes("403")) {
          errText = "Acesso Negado (403): Verifique as credenciais do seu cartão de postagem/contrato dos Correios no painel.";
        } else if (errJson.errorDescription) {
          const desc = errJson.errorDescription;
          const msgMatch = desc.match(/msgs\\?":\\?\s*\[\\?"([^"\]]+)\\?"\]/i) || desc.match(/msgs\\?":\\?\s*\["([^"\]]+)"\]/i);
          if (msgMatch && msgMatch[1]) {
            errText = msgMatch[1].replace(/\\/g, "");
          } else {
            errText = errJson.errorMessage || desc;
          }
        } else {
          errText = errJson.mensagem || errJson.msgs?.[0] || errJson.errorMessage || JSON.stringify(errJson);
        }
      } catch (e) {
        errText = await response.text();
      }
      throw new Error(`Erro na Pré-Postagem via n8n: ${errText || response.statusText}`);
    }

    const result = await response.json();

    if (!result.id && (result.msgs || result.mensagem || result.error || result.errorMessage)) {
      let errText = result.mensagem || result.msgs?.[0] || result.errorMessage;
      if (!errText && result.error) {
        if (typeof result.error === "object") {
          errText = result.error.message;
          if (errText && errText.includes(" - \"")) {
            const msgMatch = errText.match(/\d+\s*-\s*"(\{.*\})"/);
            if (msgMatch && msgMatch[1]) {
              try {
                const parsed = JSON.parse(msgMatch[1].replace(/\\/g, ""));
                errText = parsed.mensagem || parsed.msgs?.[0] || errText;
              } catch (e) {}
            }
          }
        } else {
          errText = result.error;
        }
      }
      if (!errText) {
        errText = JSON.stringify(result);
      }
      throw new Error(`Erro na Pré-Postagem: ${errText}`);
    }

    if (listaServicoAdicional.length > 0) {
      const responseServices = result.listaServicoAdicional || [];
      for (const sentService of listaServicoAdicional) {
        const found = responseServices.some(
          s => String(s.codigoServicoAdicional) === String(sentService.codigoServicoAdicional)
        );
        if (!found) {
          throw new Error(
            `Falha na contratação dos adicionais: os Correios não confirmaram a adesão do serviço ${sentService.codigoServicoAdicional} no contrato.`
          );
        }
      }
    }

    return {
      id: result.id,
      codigoObjeto: result.codigoObjeto
    };
  },

  /**
   * Solicita a geração do PDF do rótulo delegando a requisição para o n8n.
   */
  async gerarRotulo(idPrePostagem) {
    const payload = {
      idsPrePostagem: [idPrePostagem],
      tipoRotulo: "P",
      formatoRotulo: "ET"
    };

    let response = await fetch(N8N_CORREIOS_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "gerarRotulo",
        payload
      }),
    });

    if (!response.ok) {
      let errText = "";
      try {
        const errJson = await response.json();
        errText = errJson.mensagem || errJson.msgs?.[0] || JSON.stringify(errJson);
      } catch (e) {
        errText = await response.text();
      }
      throw new Error(`Erro ao gerar Rótulo via n8n: ${errText || response.statusText}`);
    }

    const result = await response.json();
    return result.idRecibo;
  },

  /**
   * Faz polling para baixar o rótulo processado delegando a requisição para o n8n.
   */
  async baixarRotulo(idRecibo) {
    const maxTentativas = 5;
    const intervaloMs = 2000;

    for (let tentativa = 1; tentativa <= maxTentativas; tentativa++) {
      try {
        let response = await fetch(N8N_CORREIOS_WEBHOOK_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "baixarRotulo",
            idRecibo
          }),
        });

        if (response.status === 202) {
          await sleep(intervaloMs);
          continue;
        }

        if (!response.ok) {
          await sleep(intervaloMs);
          continue;
        }

        const result = await response.json();
        const data = Array.isArray(result) ? result[0] : result;

        if (data && (data.mensagem || data.message || data.error || data.errorDescription)) {
          const errMsg = data.mensagem || data.message || data.error || data.errorDescription;
          throw new Error(errMsg);
        }

        if (data && data.dados) {
          return data.dados; // String base64 do PDF
        }
      } catch (err) {
        console.warn(`[CorreiosService] Tentativa ${tentativa} no n8n falhou:`, err);
        if (err.message && (err.message.includes("PPN-") || err.message.includes("Erro retornado") || err.message.startsWith("PPN-"))) {
          throw err; // Aborta o loop e propaga o erro real dos Correios para a tela do usuário
        }
      }
      await sleep(intervaloMs);
    }

    throw new Error("Rótulo sendo processado pelos Correios, tente reimprimir em alguns instantes.");
  },

  /**
   * Converte a string base64 em um PDF Blob e o abre em uma nova aba do navegador.
   */
  async abrirEtiquetaPDF(base64) {
    let finalBase64 = base64;
    try {
      const { PDFDocument } = await import("pdf-lib");
      const CM_TO_PT = 28.3465;

      const pdfBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const srcDoc = await PDFDocument.load(pdfBytes);
      const [srcPage] = srcDoc.getPages();
      const { width, height } = srcPage.getSize();
      
      console.log(`[CorreiosService] Recortando etiqueta A4 original (${width.toFixed(1)}x${height.toFixed(1)} pt) para 10x15cm...`);

      // Coordenadas calibradas para extrair exatamente a moldura preta da etiqueta oficial na A4
      // O Y cresce de baixo para cima no PDF
      const cropBox = {
        left: 10, // borda esquerda da moldura
        bottom: height - 428, // base da moldura (altura de ~148mm a partir do topo)
        right: 298, // borda direita da moldura (~101mm de largura)
        top: height - 8 // topo da moldura
      };

      console.log(`[CorreiosService] CropBox aplicada:`, cropBox);

      const outDoc = await PDFDocument.create();
      const embedded = await outDoc.embedPage(srcPage, cropBox);

      const larguraPt = 10 * CM_TO_PT; // 283.46 pt (10cm)
      const alturaPt = 15 * CM_TO_PT;  // 425.20 pt (15cm)
      const page = outDoc.addPage([larguraPt, alturaPt]);

      page.drawPage(embedded, {
        x: 0,
        y: 0,
        width: larguraPt,
        height: alturaPt,
      });

      const savedBytes = await outDoc.save();
      
      let binary = '';
      const len = savedBytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(savedBytes[i]);
      }
      finalBase64 = btoa(binary);
      console.log("[CorreiosService] Etiqueta 10x15cm processada e recortada com sucesso via pdf-lib!");
    } catch (cropError) {
      console.warn("[CorreiosService] Não foi possível recortar o PDF (usando original A4 como fallback):", cropError);
    }

    try {
      const byteCharacters = atob(finalBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch (error) {
      console.error("[CorreiosService] Falha ao converter base64 para PDF Blob:", error);
      throw new Error("Não foi possível abrir o PDF. Verifique se o bloqueador de pop-ups está ativado.");
    }
  },

  async consultarStatusPrePostagem(prepostagemId) {
    const response = await fetch(N8N_CORREIOS_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "consultarReversa",
        payload: {
          codigoObjeto: prepostagemId
        }
      }),
    });

    if (!response.ok) {
      throw new Error("Erro de rede ao consultar status nos Correios.");
    }

    const result = await response.json();
    if (result.error) {
      throw new Error(result.mensagem || "Erro ao consultar status nos Correios.");
    }

    return result;
  },

  async consultarSro(codigoObjeto) {
    const response = await fetch(N8N_CORREIOS_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "consultarSro",
        payload: {
          codigoObjeto: codigoObjeto
        }
      }),
    });

    if (!response.ok) {
      throw new Error("Erro de rede ao consultar rastreamento SRO nos Correios.");
    }

    const result = await response.json();
    if (result.error) {
      throw new Error(result.mensagem || "Erro ao consultar rastreamento SRO nos Correios.");
    }

    return result;
  }
};
